﻿import { SortDirection, StatusFilterValue } from '~/modules/covenants/types.ts';
import type { TApplicationStatus } from '~/modules/covenants/types.ts';
import type {
  TManagerApplicationFilters,
  TManagerChildPeriodOption,
  TManagerApplicationSortKey,
} from '~/modules/covenants/CovenantsDataForm/CovenantsFormByManager/types.ts';
import type { IStatusConfig } from '~/modules/covenants/statusConfig.ts';
import { IReadyCovenantTargetStatusConfig } from '~/modules/covenants/CovenantsDataForm/CovenantsFormByManager/utils/utils.ts';

export const DEFAULT_MANAGER_APPLICATION_FILTERS: TManagerApplicationFilters = {
  status: [],
  assignee: [],
  segmentIds: [],
  sortKey: 'sentAt',
  sortDirection: SortDirection.Desc,
  page: 0,
  rowsPerPage: 10,
};

export const MANAGER_APPLICATION_STATUS_ORDER: Record<string, number> = {
  NEW: 0,
  SENT: 1,
  ACCEPTED: 2,
  DENIED: 3,
  PARTLY_ACCEPTED: 4,
};
export const MANAGER_UK_LOCALE = 'uk';
export const MANAGER_UK_LOCALE_COMPARE_OPTIONS = { sensitivity: 'base' } as const;

export const DATE_FILTER_TITLE = 'Вибір дати періоду';
export const START_DATE_LABEL = 'З дати';
export const END_DATE_LABEL = 'По дату';
export const DATE_PLACEHOLDER = 'дд.мм.рррр';
export const DOCUMENT_PERIOD_FILTER_TITLE = 'Фільтр за документом і контрольним періодом';
export const DOCUMENT_TYPE_LABEL = 'Тип документа';
export const YEAR_PERIOD_LABEL = 'Рік';
export const CHILD_PERIOD_LABEL = 'Період контролю';
export const TERM_START_DATE_LABEL = 'Термін з';
export const TERM_END_DATE_LABEL = 'Термін до';
export const CLEAR_TERM_FILTER_BUTTON_LABEL = 'Очистити термін';
export const DOCUMENT_TYPE_NO_OPTIONS_TEXT = 'Тип документу не знайдено';
export const DOCUMENT_TYPE_LOADING_TEXT = 'Завантаження типів документів...';
export const CHILD_PERIOD_NO_OPTIONS_TEXT = 'Період не знайдено';
export const MANAGER_CHILD_PERIOD_OPTIONS: TManagerChildPeriodOption[] = [
  { value: 'Q1', label: 'I квартал' },
  { value: 'Q2', label: 'II квартал' },
  { value: 'Q3', label: 'III квартал' },
  { value: 'Q4', label: 'IV квартал' },
  { value: 'JANUARY', label: 'Січень' },
  { value: 'FEBRUARY', label: 'Лютий' },
  { value: 'MARCH', label: 'Березень' },
  { value: 'APRIL', label: 'Квітень' },
  { value: 'MAY', label: 'Травень' },
  { value: 'JUNE', label: 'Червень' },
  { value: 'JULY', label: 'Липень' },
  { value: 'AUGUST', label: 'Серпень' },
  { value: 'SEPTEMBER', label: 'Вересень' },
  { value: 'OCTOBER', label: 'Жовтень' },
  { value: 'NOVEMBER', label: 'Листопад' },
  { value: 'DECEMBER', label: 'Грудень' },
];
export const MANAGER_CHILD_PERIOD_OPTIONS_BY_VALUE: Record<
  string,
  TManagerChildPeriodOption
> = Object.fromEntries(
  MANAGER_CHILD_PERIOD_OPTIONS.map((option) => [option.value, option])
);
export const DOWNLOAD_REPORT_BUTTON_LABEL = 'Завантажити звіт';
export const DOWNLOAD_REPORT_BUTTON_LOADING_LABEL = 'Завантаження...';
export const DOWNLOAD_REPORT_BUTTON_TOOLTIP =
  'Щоб завантажити звіт, вкажіть початкову або кінцеву дату періоду чи терміну.';
export const CLEAR_DATE_FILTER_BUTTON_LABEL = 'Очистити';
export const EXPORT_DATE_FORMAT = 'YYYY-MM-DD-HH-mm';
export const EXPORT_FILENAME_PREFIX = 'Заявки-клієнтів';
export const EXPORT_FALLBACK_EXTENSION = '.xlsx';

export const MANAGER_STATUS_OPTIONS = [
  StatusFilterValue.Sent,
  StatusFilterValue.Accepted,
  StatusFilterValue.Denied,
  StatusFilterValue.PartlyAccepted,
] as const;

export const MANAGER_FINAL_STATUSES: ReadonlySet<TApplicationStatus> = new Set([
  StatusFilterValue.Denied,
  StatusFilterValue.PartlyAccepted,
  StatusFilterValue.Accepted,
]);

export const MANAGER_APPLICATION_SORTABLE_COLUMNS: ReadonlyArray<{
  label: string;
  sortKey: TManagerApplicationSortKey;
}> = [
  { label: 'ЄДРПОУ', sortKey: 'contragentCode' },
  { label: 'Коротка назва', sortKey: 'contragentShortName' },
  { label: 'Повна назва', sortKey: 'contragentFullName' },
  { label: 'Отримано', sortKey: 'sentAt' },
];

export const MANAGER_STATUS_COLUMN = {
  label: 'Статус',
  sortKey: 'status' as const,
};

export const MANAGER_ASSIGNEE_COLUMN = {
  label: 'Виконавець',
  sortKey: 'assignee' as const,
};

export const MANAGER_APPLICATION_TABLE_TITLE = 'Заявки клієнтів';
export const MANAGER_APPLICATION_EMPTY_TEXT = 'Заявки відсутні';
export const MANAGER_APPLICATION_PAGE_TITLE = 'Заявки менеджера';
export const MANAGER_APPLICATION_PAGE_SUBTITLE = 'Перегляд усіх наданих заявок';
export const MANAGER_APPLICATION_SEARCH_PLACEHOLDER =
  'Пошук за ЄДРПОУ або короткою / повною назвою';
export const MANAGER_APPLICATION_SEARCH_BUTTON_ARIA_LABEL = 'Запустити пошук заявок';
export const MANAGER_APPLICATION_CLEAR_SEARCH_BUTTON_ARIA_LABEL = 'Очистити пошук заявок';
export const MANAGER_ALL_STATUSES_LABEL = 'Усі статуси';
export const MANAGER_ALL_ASSIGNEES_LABEL = 'Усі виконавці';
export const MANAGER_ALL_SEGMENTS_LABEL = 'Усі сегменти';
export const MANAGER_ROWS_PER_PAGE_LABEL = 'Рядків:';
export const MANAGER_APPLICATION_ROWS_PER_PAGE_OPTIONS = [5, 10, 25];
export const MANAGER_APPLICATION_TABLE_MIN_WIDTH = 1540;
export const MANAGER_APPLICATION_EMPTY_VALUE = '-';
export const MANAGER_APPLICATIONS_URL_MAX_SEARCH_LENGTH = 200;
export const MANAGER_APPLICATIONS_URL_PARAMS = {
  page: 'p',
  pageSize: 'ps',
  search: 'q',
  fromDate: 'fd',
  toDate: 'td',
  sortKey: 'sk',
  sortDirection: 'sd',
  status: 's',
  assignee: 'a',
  segmentIds: 'sg',
  documentIds: 'di',
  yearPeriod: 'yp',
  childPeriod: 'cp',
  termStartDate: 'tsd',
  termEndDate: 'ted',
} as const;
export const MANAGER_APPLICATION_REVIEW_PATH = '/covenants/manager/applications';
export const MANAGER_APPLICATION_REVIEW_DOCUMENTS_ROWS_PER_PAGE_OPTIONS = [5, 10, 25, 50];
export const MANAGER_APPLICATION_REVIEW_EMPTY_TEXT = 'Документи відсутні';
export const MANAGER_APPLICATION_REVIEW_REJECT_ALL_BUTTON_LABEL = 'Відкликати всі';
export const MANAGER_APPLICATION_REVIEW_SUBMIT_BUTTON_LABEL = 'Підтвердити';
export const MANAGER_APPLICATION_REVIEW_DONE_BUTTON_LABEL = 'Готово';
export const MANAGER_APPLICATION_REVIEW_SUBMITTING_BUTTON_LABEL = 'Збереження...';
export const MANAGER_APPLICATION_REVIEW_SUCCESS_TEXT = 'Рішення по заявці успішно збережено.';
export const MANAGER_APPLICATION_REVIEW_SUCCESS_BUTTON_LABEL = 'До списку заявок';
export const MANAGER_APPLICATION_REVIEW_MISSING_ID_TEXT =
  'Не вдалося визначити ідентифікатор заявки.';
export const MANAGER_APPLICATION_REVIEW_TITLE_PREFIX = 'Заявка №';
export const MANAGER_APPLICATION_REVIEW_ID_PREFIX = 'ID заявки: ';
export const MANAGER_APPLICATION_REVIEW_SUBTITLE_SEPARATOR = ' | ';

export const MANAGER_APPLICATION_REVIEW_TABLE_LABELS = {
  documentName: 'Назва документу',
  term: 'Термін',
  status: 'Статус',
  comment: 'Коментар',
  signer: 'Підписант',
  period: 'Період',
  preview: 'Перегляд',
  accept: 'Акцептувати',
  reject: 'Відхилити',
  previewButton: 'Переглянути',
  viewed: 'Переглянуто',
  notViewed: 'Не переглянуто',
} as const;

export const MANAGER_APPLICATION_ACTIONS_BUTTON_LABEL = 'Обрати дію';
export const MANAGER_APPLICATION_ACTION_VIEW_LABEL = 'Переглянути заявку';
export const MANAGER_APPLICATION_ACTION_COVENANTS_LABEL = 'Ковенанти';
export const MANAGER_APPLICATION_ACTION_ASSIGN_LABEL = 'Взяти в роботу';
export const MANAGER_APPLICATION_ACTION_UNASSIGN_LABEL = 'Відмовитись';
export const MANAGER_APPLICATION_ACTION_IN_PROGRESS_LABEL = 'Виконується...';
export const MANAGER_APPLICATION_ASSIGNMENT_MISSING_ID_TEXT =
  'Не вдалося визначити контрагента для цієї заявки.';
export const MANAGER_APPLICATION_REQUEST_DATE_FORMAT = 'YYYY-MM-DD';

export const MANAGER_APPLICATION_COVENANTS_ALLOWED_STATUSES: ReadonlySet<TApplicationStatus> =
  new Set([StatusFilterValue.Accepted, StatusFilterValue.PartlyAccepted]);

export const MANAGER_READY_COVENANTS_DEFAULT_PAGE_SIZE = 5;
export const MANAGER_READY_COVENANTS_DOCUMENTS_BATCH_SIZE = 5;
export const MANAGER_READY_COVENANTS_COPY_RESET_TIMEOUT_MS = 1500;
export const MANAGER_READY_COVENANTS_EMPTY_VALUE = '-';

export const MANAGER_READY_COVENANTS_DIALOG_TITLE = 'Ковенанти';
export const MANAGER_READY_COVENANTS_EMPTY_APPLICATION_TEXT = 'Заявку не обрано.';
export const MANAGER_READY_COVENANTS_EMPTY_LIST_TEXT = 'Ковенанти відсутні.';
export const MANAGER_READY_COVENANTS_CLOSE_BUTTON_TEXT = 'Закрити';

export const MANAGER_READY_COVENANT_STATUS_LABELS: Record<string, string> = {
  WAITING_FOR_DOCS: 'Очікує документи',
  CAN_BE_CLOSED: 'Можна закрити',
};

export const MANAGER_READY_COVENANT_DOCUMENT_STATUS_FALLBACK_LABELS: Record<string, string> = {
  IN_PROGRESS: 'Обробка',
  PENDING: 'Надати',
  WAITING_FOR_DOCS: 'Очікує документи',
  CAN_BE_CLOSED: 'Можна закрити',
};

export const MANAGER_READY_COVENANT_MONITORING_ID_LABEL = 'ID моніторингу:';
export const MANAGER_READY_COVENANT_CONTRACT_LABEL = 'Контракт:';
export const MANAGER_READY_COVENANT_PERIOD_LABEL = 'Період:';
export const MANAGER_READY_COVENANT_DEADLINE_LABEL = 'Термін:';
export const MANAGER_READY_COVENANT_STATUS_LABEL = 'Статус:';
export const MANAGER_READY_COVENANT_TARGET_STATUS_LABEL = 'Цільовий статус:';

export const MANAGER_READY_COVENANT_COPY_TOOLTIP_TEXT = 'Скопіювати ID';
export const MANAGER_READY_COVENANT_COPIED_TOOLTIP_TEXT = 'Скопійовано';
export const MANAGER_READY_COVENANT_COPY_ARIA_LABEL = 'Скопіювати ID моніторингу';

export const MANAGER_READY_COVENANT_LOAD_DOCUMENTS_BUTTON_TEXT = 'Завантажити документи';
export const MANAGER_READY_COVENANT_LOAD_MORE_BUTTON_TEXT = 'Завантажити ще';

export const MANAGER_READY_COVENANT_NO_RELATED_DOCUMENTS_TEXT =
  "Для цього ковенанта немає пов'язаних документів.";

export const MANAGER_READY_COVENANT_DOCUMENT_NAME_HEADER = 'Назва документа';
export const MANAGER_READY_COVENANT_DOCUMENT_STATUS_HEADER = 'Статус документа';

export const READY_COVENANT_STATUS_CONFIGS: Record<string, IStatusConfig> = {
  WAITING_FOR_DOCS: {
    label: MANAGER_READY_COVENANT_STATUS_LABELS.WAITING_FOR_DOCS || '',
    color: 'warning',
    description: 'Очікується надання документів для подальшого розгляду.',
  },
  CAN_BE_CLOSED: {
    label: MANAGER_READY_COVENANT_STATUS_LABELS.CAN_BE_CLOSED || '',
    color: 'success',
    description: 'Ковенант можна закривати.',
  },
};

export const READY_DOCUMENT_STATUS_FALLBACK_CONFIGS: Record<string, IStatusConfig> = {
  IN_PROGRESS: {
    label: MANAGER_READY_COVENANT_DOCUMENT_STATUS_FALLBACK_LABELS.IN_PROGRESS || '',
    color: 'default',
    description: 'Документ передано на обробку.',
  },
  PENDING: {
    label: MANAGER_READY_COVENANT_DOCUMENT_STATUS_FALLBACK_LABELS.PENDING || '',
    color: 'error',
    description: 'Документ потрібно надати.',
  },
  WAITING_FOR_DOCS: {
    label: MANAGER_READY_COVENANT_DOCUMENT_STATUS_FALLBACK_LABELS.WAITING_FOR_DOCS || '',
    color: 'warning',
    description: 'Очікується надання документів.',
  },
  CAN_BE_CLOSED: {
    label: MANAGER_READY_COVENANT_DOCUMENT_STATUS_FALLBACK_LABELS.CAN_BE_CLOSED || '',
    color: 'success',
    description: 'Документ готовий, можна закривати.',
  },
};

export const READY_COVENANT_TARGET_STATUS_CONFIGS: Record<
  string,
  Omit<IReadyCovenantTargetStatusConfig, 'label'>
> = {
  активна: {
    color: 'success',
    description: '',
    variant: 'outlinedSuccess',
  },
  доопрацювання: {
    color: 'error',
    description: '',
    variant: 'outlinedError',
  },
  виконана: {
    color: 'success',
    description: '',
    variant: 'success',
  },
  порушена: {
    color: 'error',
    description: '',
    variant: 'error',
  },
  врегульована: {
    color: 'warning',
    description: '',
    variant: 'warning',
  },
};
